import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import AdminInvites from "../components/AdminInvites";
import AdminDiscountCodes from "../components/AdminDiscountCodes";
import AdminUsers from "../components/AdminUsers";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { 
  Zap, 
  Upload, 
  User, 
  LogOut, 
  Activity, 
  TrendingUp, 
  Calendar,
  ChevronRight,
  ChevronDown,
  FileText,
  Search,
  Filter,
  X,
  Plus,
  Users,
  Edit,
  Scale,
  Ruler,
  Loader2,
  Check,
  Link2,
  Crown,
  Percent,
  UsersRound
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { t } = useTranslation();
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  const [profile, setProfile] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTestType, setFilterTestType] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // "list", "athletes", or "admin"
  const [expandedAthletes, setExpandedAthletes] = useState({});
  
  // Athlete profiles
  const [athleteProfiles, setAthleteProfiles] = useState({});
  
  // Edit profile dialog
  const [editingAthlete, setEditingAthlete] = useState(null);
  const [profileForm, setProfileForm] = useState({
    birth_date: "",
    weight_kg: "",
    height_cm: "",
    gender: "male"
  });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const [analysesRes, profileRes, paymentRes, athletesRes] = await Promise.all([
        fetch(`${API}/analysis/list`, { credentials: "include", headers }),
        fetch(`${API}/profile`, { credentials: "include", headers }),
        fetch(`${API}/payment/status`, { credentials: "include", headers }),
        fetch(`${API}/athletes`, { credentials: "include", headers }),
      ]);

      if (analysesRes.ok) {
        const data = await analysesRes.json();
        setAnalyses(data.analyses || []);
      }

      if (profileRes.ok) {
        const data = await profileRes.json();
        setProfile(data.profile);
      }

      if (paymentRes.ok) {
        const data = await paymentRes.json();
        setPaymentStatus(data);
        // Check if user is admin
        setIsAdmin(data.is_admin || false);
      }
      
      if (athletesRes.ok) {
        const data = await athletesRes.json();
        // Convert array to object keyed by athlete_name
        const profilesMap = {};
        (data.athletes || []).forEach(p => {
          profilesMap[p.athlete_name] = p;
        });
        setAthleteProfiles(profilesMap);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Helper function to format date without timezone shift
  // This prevents the date from being off by one day due to UTC conversion
  const formatDateLocal = (dateString, options = {}) => {
    if (!dateString) return "";
    // Parse the date string directly without timezone conversion
    const parts = dateString.split("-");
    if (parts.length === 3) {
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1; // Month is 0-indexed
      const day = parseInt(parts[2]);
      const date = new Date(year, month, day);
      return date.toLocaleDateString("de-DE", options);
    }
    // Fallback for other date formats
    return new Date(dateString).toLocaleDateString("de-DE", options);
  };

  // Filter and group analyses
  const filteredAnalyses = useMemo(() => {
    return analyses.filter(analysis => {
      // Search filter - check athlete name and test date (or created_at as fallback)
      const testDateStr = analysis.athlete_data?.test_date 
        ? formatDateLocal(analysis.athlete_data.test_date)
        : new Date(analysis.created_at).toLocaleDateString("de-DE");
      
      const matchesSearch = searchTerm === "" || 
        (analysis.athlete_name && analysis.athlete_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        testDateStr.includes(searchTerm);
      
      // Test type filter
      const matchesType = filterTestType === "all" || analysis.test_type === filterTestType;
      
      return matchesSearch && matchesType;
    });
  }, [analyses, searchTerm, filterTestType]);

  // Group analyses by athlete
  const athleteGroups = useMemo(() => {
    const groups = {};
    filteredAnalyses.forEach(analysis => {
      const athleteName = analysis.athlete_name && analysis.athlete_name !== "Unbenannt" 
        ? analysis.athlete_name 
        : "Unbenannt";
      
      if (!groups[athleteName]) {
        groups[athleteName] = [];
      }
      groups[athleteName].push(analysis);
    });
    
    // Sort each group by date (newest first)
    Object.keys(groups).forEach(name => {
      groups[name].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });
    
    return groups;
  }, [filteredAnalyses]);

  const toggleAthleteExpanded = (athleteName) => {
    setExpandedAthletes(prev => ({
      ...prev,
      [athleteName]: !prev[athleteName]
    }));
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterTestType("all");
  };

  const hasActiveFilters = searchTerm !== "" || filterTestType !== "all";

  const latestAnalysis = analyses[0];

  // Calculate FTP progression for an athlete
  const getFTPProgression = (athleteAnalyses) => {
    if (athleteAnalyses.length < 2) return null;
    const latest = athleteAnalyses[0].ftp;
    const previous = athleteAnalyses[1].ftp;
    const diff = Math.round((latest - previous) * 10) / 10; // Round to 1 decimal
    const percentage = ((diff / previous) * 100).toFixed(1);
    return { diff, percentage, improved: diff > 0 };
  };

  // Calculate age from birth date
  const getAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    // Parse date without timezone conversion
    const parts = birthDate.split("-");
    let birth;
    if (parts.length === 3) {
      birth = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    } else {
      birth = new Date(birthDate);
    }
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Open edit profile dialog
  const openEditProfile = (athleteName) => {
    const existingProfile = athleteProfiles[athleteName];
    if (existingProfile) {
      setProfileForm({
        birth_date: existingProfile.birth_date || "",
        weight_kg: existingProfile.weight_kg || "",
        height_cm: existingProfile.height_cm || "",
        gender: existingProfile.gender || "male"
      });
    } else {
      setProfileForm({
        birth_date: "",
        weight_kg: "",
        height_cm: "",
        gender: "male"
      });
    }
    setEditingAthlete(athleteName);
  };

  // Save athlete profile
  const saveAthleteProfile = async () => {
    if (!editingAthlete) return;
    
    if (!profileForm.birth_date || !profileForm.weight_kg || !profileForm.height_cm) {
      toast.error("Bitte fülle alle Pflichtfelder aus");
      return;
    }
    
    setSavingProfile(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` })
      };
      
      const response = await fetch(`${API}/athletes`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({
          athlete_name: editingAthlete,
          birth_date: profileForm.birth_date,
          weight_kg: parseFloat(profileForm.weight_kg),
          height_cm: parseFloat(profileForm.height_cm),
          gender: profileForm.gender
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAthleteProfiles(prev => ({
          ...prev,
          [editingAthlete]: data.profile
        }));
        setEditingAthlete(null);
        toast.success("Athletenprofil gespeichert!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Fehler beim Speichern");
      }
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="flex items-center gap-2">
              <Zap className="h-6 w-6 sm:h-8 sm:w-8 text-volt" />
              <span className="font-display font-bold text-lg sm:text-xl tracking-tight">VELOMETRICS</span>
              <span className="text-xs text-muted-foreground hidden md:inline">by Loops Cycling Academy</span>
            </Link>
            <div className="flex items-center gap-2 sm:gap-4">
              <LanguageSwitcher variant="minimal" />
              <Link to="/profile" className="nav-link text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">{t('nav.profile')}</span>
              </Link>
              <Button 
                variant="ghost" 
                onClick={handleLogout}
                className="text-muted-foreground hover:text-white px-2 sm:px-4"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('nav.logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-18 sm:pt-24 pb-8 sm:pb-12 px-3 sm:px-6 lg:px-8" data-testid="dashboard-main">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <p className="data-label mb-1 sm:mb-2 text-xs sm:text-sm">{t('dashboard.title')}</p>
            <h1 className="font-display text-xl sm:text-2xl md:text-3xl font-bold tracking-tight uppercase">
              {t('dashboard.welcome')}, <span className="text-volt">{user?.name || t('dashboard.athlete', 'Athlet')}</span>
            </h1>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
            {/* Subscription Status */}
            <div className="glass-card rounded-xl p-4 sm:p-6 stat-card">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <Activity className="h-5 w-5 sm:h-6 sm:w-6 text-volt" />
                {paymentStatus?.is_active ? (
                  <span className="text-xs text-volt font-medium">{t('dashboard.active', 'Aktiv')}</span>
                ) : (
                  <span className="text-xs text-carb-orange font-medium">{t('dashboard.inactive', 'Inaktiv')}</span>
                )}
              </div>
              <h3 className="font-display font-bold uppercase mb-2 text-sm sm:text-base">{t('dashboard.subscription', 'Abonnement')}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                {paymentStatus?.is_active 
                  ? paymentStatus.subscription_type === "subscription" 
                    ? `${t('pricing.subscription.title')} - ${paymentStatus.analyses_remaining || 0} ${t('dashboard.analysesRemaining', 'Analysen übrig')}` 
                    : `${t('pricing.single.title')} - ${paymentStatus.analyses_remaining || 0} ${t('dashboard.remaining', 'übrig')}`
                  : t('dashboard.noActiveSubscription', 'Kein aktives Abonnement')}
              </p>
              <Link to="/pricing" data-testid="subscription-link">
                <Button variant="outline" size="sm" className="border-white/20 hover:bg-white/10 text-xs sm:text-sm">
                  {paymentStatus?.is_active ? t('dashboard.manage', 'Verwalten') : t('dashboard.choosePlan', 'Plan wählen')}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>

            {/* New Analysis */}
            <div className="glass-card rounded-xl p-4 sm:p-6 stat-card border border-volt/20">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <Upload className="h-5 w-5 sm:h-6 sm:w-6 text-volt" />
                <span className="text-xs text-volt font-medium">{t('dashboard.new', 'Neu')}</span>
              </div>
              <h3 className="font-display font-bold uppercase mb-2 text-sm sm:text-base">{t('dashboard.newAnalysis')}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                {t('dashboard.uploadDesc', 'Lade deine .FIT-Datei hoch und starte eine neue Analyse.')}
              </p>
              <Link to="/upload" data-testid="upload-link">
                <Button className="skew-btn bg-volt text-black hover:bg-volt/90 font-bold uppercase tracking-wider text-xs sm:text-sm">
                  <span>{t('dashboard.startAnalysis', 'Hochladen')}</span>
                </Button>
              </Link>
            </div>
          </div>

          {/* Latest Analysis */}
          {latestAnalysis && (
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h2 className="font-display text-lg sm:text-xl font-bold uppercase">{t('dashboard.lastAnalysis', 'Letzte Analyse')}</h2>
                <Link to={`/analysis/${latestAnalysis.analysis_id}`} className="text-volt text-xs sm:text-sm hover:underline">
                  {t('dashboard.viewDetails', 'Details ansehen')}
                </Link>
              </div>
              <div className="glass-card rounded-xl p-4 sm:p-6 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
                <div>
                  <p className="data-label text-xs">FTP</p>
                  <p className="number-display text-xl sm:text-3xl text-volt">{Number(latestAnalysis.ftp).toFixed(0)}<span className="text-xs sm:text-sm ml-1">W</span></p>
                  {latestAnalysis.ftp_per_kg && (
                    <p className="text-xs text-muted-foreground">{Number(latestAnalysis.ftp_per_kg).toFixed(2)} W/kg</p>
                  )}
                </div>
                <div>
                  <p className="data-label text-xs">VO2max</p>
                  <p className="number-display text-xl sm:text-3xl text-cyan">{Number(latestAnalysis.vo2max).toFixed(1)}<span className="text-xs sm:text-sm ml-1">ml/kg</span></p>
                  {latestAnalysis.vo2max_absolute && (
                    <p className="text-xs text-muted-foreground">{Number(latestAnalysis.vo2max_absolute).toFixed(1)} L/min</p>
                  )}
                </div>
                <div>
                  <p className="data-label text-xs">VT1</p>
                  <p className="number-display text-xl sm:text-3xl text-carb-orange">{Number(latestAnalysis.vt1).toFixed(0)}<span className="text-xs sm:text-sm ml-1">W</span></p>
                </div>
                <div>
                  <p className="data-label text-xs">VT2</p>
                  <p className="number-display text-xl sm:text-3xl text-heart-red">{Number(latestAnalysis.vt2).toFixed(0)}<span className="text-xs sm:text-sm ml-1">W</span></p>
                </div>
              </div>
            </div>
          )}

          {/* Analysis History with Filters */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4">
              <h2 className="font-display text-lg sm:text-xl font-bold uppercase">
                {viewMode === "admin" ? t('dashboard.adminTabs.invites') : viewMode === "discounts" ? t('dashboard.adminTabs.discounts') : viewMode === "users" ? t('dashboard.adminTabs.users') : t('dashboard.yourAnalyses', 'Analysen')}
              </h2>
              
              {/* View Mode Toggle */}
              <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                <Button
                  variant={viewMode === "list" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className={`${viewMode === "list" ? "bg-volt text-black" : "border-white/20"} text-xs sm:text-sm px-2 sm:px-3`}
                  data-testid="view-list-btn"
                >
                  <FileText className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Liste</span>
                </Button>
                <Button
                  variant={viewMode === "athletes" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setViewMode("athletes")}
                  className={`${viewMode === "athletes" ? "bg-volt text-black" : "border-white/20"} text-xs sm:text-sm px-2 sm:px-3`}
                  data-testid="view-athletes-btn"
                >
                  <Users className="h-4 w-4 mr-1" />
                  Athleten
                </Button>
                {isAdmin && (
                  <>
                  <Button
                    variant={viewMode === "admin" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setViewMode("admin")}
                    className={viewMode === "admin" ? "bg-cyan text-black" : "border-cyan/30 text-cyan"}
                    data-testid="view-admin-btn"
                  >
                    <Crown className="h-4 w-4 mr-1" />
                    Einladungen
                  </Button>
                  <Button
                    variant={viewMode === "discounts" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setViewMode("discounts")}
                    className={viewMode === "discounts" ? "bg-carb-orange text-black" : "border-carb-orange/30 text-carb-orange"}
                    data-testid="view-discounts-btn"
                  >
                    <Percent className="h-4 w-4 mr-1" />
                    Rabatte
                  </Button>
                  <Button
                    variant={viewMode === "users" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setViewMode("users")}
                    className={viewMode === "users" ? "bg-volt text-black" : "border-volt/30 text-volt"}
                    data-testid="view-users-btn"
                  >
                    <UsersRound className="h-4 w-4 mr-1" />
                    Nutzer
                  </Button>
                  </>
                )}
              </div>
            </div>

            {/* Admin View - Invites */}
            {viewMode === "admin" && isAdmin && (
              <AdminInvites token={token} />
            )}

            {/* Admin View - Discount Codes */}
            {viewMode === "discounts" && isAdmin && (
              <AdminDiscountCodes token={token} />
            )}

            {/* Admin View - Users */}
            {viewMode === "users" && isAdmin && (
              <AdminUsers token={token} />
            )}

            {/* Search and Filter Bar - only show for list/athletes view */}
            {viewMode !== "admin" && viewMode !== "discounts" && viewMode !== "users" && (
            <div className="glass-card rounded-xl p-4 mb-4">
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Search Input */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Suche nach Athletenname oder Datum..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-zinc-900 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-volt/50"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-white"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Filter Toggle */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className={`border-white/20 ${showFilters ? 'bg-white/10' : ''}`}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                  {hasActiveFilters && (
                    <span className="ml-2 w-2 h-2 bg-volt rounded-full"></span>
                  )}
                </Button>

                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-muted-foreground hover:text-white"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Zurücksetzen
                  </Button>
                )}
              </div>

              {/* Expanded Filters */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Testtyp</label>
                    <select
                      value={filterTestType}
                      onChange={(e) => setFilterTestType(e.target.value)}
                      className="bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-volt/50"
                    >
                      <option value="all">Alle Tests</option>
                      <option value="20min">20-Minuten Test</option>
                      <option value="stufentest">Stufentest</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Results Count - only show for list/athletes view */}
            {viewMode !== "admin" && viewMode !== "discounts" && (
            <>
            <div className="text-sm text-muted-foreground mb-4">
              {filteredAnalyses.length} von {analyses.length} Analysen
              {viewMode === "athletes" && ` • ${Object.keys(athleteGroups).length} Athleten`}
            </div>

            {loading ? (
              <div className="glass-card rounded-xl p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-volt mx-auto"></div>
              </div>
            ) : filteredAnalyses.length > 0 ? (
              viewMode === "list" ? (
                /* List View */
                <div className="space-y-3">
                  {filteredAnalyses.map((analysis) => (
                    <Link 
                      key={analysis.analysis_id} 
                      to={`/analysis/${analysis.analysis_id}`}
                      className="glass-card rounded-xl p-4 flex items-center justify-between hover:border-volt/30 transition-colors block"
                      data-testid={`analysis-item-${analysis.analysis_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-lg bg-volt/10 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-volt" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {analysis.athlete_name && analysis.athlete_name !== "Unbenannt" 
                              ? analysis.athlete_name 
                              : (analysis.test_type === "20min" ? "20-Minuten Test" : "Stufentest")}
                          </p>
                          <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <Calendar className="h-3 w-3" />
                            {/* Show test date if available, otherwise created_at */}
                            {analysis.athlete_data?.test_date 
                              ? formatDateLocal(analysis.athlete_data.test_date)
                              : new Date(analysis.created_at).toLocaleDateString("de-DE")}
                            {analysis.athlete_data?.test_time && (
                              <span className="text-muted-foreground">
                                {analysis.athlete_data.test_time} Uhr
                              </span>
                            )}
                            {analysis.athlete_name && analysis.athlete_name !== "Unbenannt" && (
                              <span className="ml-2">• {analysis.test_type === "20min" ? "20min" : "Stufen"}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="data-label">FTP</p>
                          <p className="number-display text-xl text-volt">{Number(analysis.ftp).toFixed(2)}W</p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                /* Athletes View */
                <div className="space-y-4">
                  {Object.entries(athleteGroups).map(([athleteName, athleteAnalyses]) => {
                    const isExpanded = expandedAthletes[athleteName] !== false; // Default expanded
                    const latestFTP = athleteAnalyses[0]?.ftp;
                    const progression = getFTPProgression(athleteAnalyses);
                    
                    return (
                      <div key={athleteName} className="glass-card rounded-xl overflow-hidden">
                        {/* Athlete Header */}
                        <button
                          onClick={() => toggleAthleteExpanded(athleteName)}
                          className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div className="h-12 w-12 rounded-full bg-volt/10 flex items-center justify-center">
                              <User className="h-6 w-6 text-volt" />
                            </div>
                            <div className="text-left">
                              <p className="font-display font-bold text-lg">{athleteName}</p>
                              <p className="text-sm text-muted-foreground">
                                {athleteAnalyses.length} {athleteAnalyses.length === 1 ? 'Test' : 'Tests'}
                                {athleteProfiles[athleteName] && (
                                  <span className="ml-2">
                                    • {athleteProfiles[athleteName].weight_kg} kg 
                                    • {getAge(athleteProfiles[athleteName].birth_date)} J.
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <p className="data-label">Aktuelle FTP</p>
                              <p className="number-display text-2xl text-volt">{latestFTP}W</p>
                              {progression && (
                                <p className={`text-xs ${progression.improved ? 'text-green-400' : 'text-red-400'}`}>
                                  {progression.improved ? '+' : ''}{progression.diff}W ({progression.improved ? '+' : ''}{progression.percentage}%)
                                </p>
                              )}
                            </div>
                            <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </div>
                        </button>

                        {/* Athlete Tests */}
                        {isExpanded && (
                          <div className="border-t border-white/10">
                            {/* Athlete Profile Info */}
                            <div className="p-4 px-6 bg-zinc-900/50 border-b border-white/10">
                              {athleteProfiles[athleteName] ? (
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-6 text-sm">
                                    <div className="flex items-center gap-1">
                                      <Calendar className="h-3 w-3 text-muted-foreground" />
                                      <span>{formatDateLocal(athleteProfiles[athleteName].birth_date)}</span>
                                      <span className="text-muted-foreground">({getAge(athleteProfiles[athleteName].birth_date)} J.)</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Scale className="h-3 w-3 text-muted-foreground" />
                                      <span>{athleteProfiles[athleteName].weight_kg} kg</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Ruler className="h-3 w-3 text-muted-foreground" />
                                      <span>{athleteProfiles[athleteName].height_cm} cm</span>
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEditProfile(athleteName);
                                    }}
                                    className="text-volt text-xs h-8"
                                  >
                                    <Edit className="h-3 w-3 mr-1" />
                                    Bearbeiten
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-muted-foreground">Kein Athletenprofil vorhanden</span>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEditProfile(athleteName);
                                    }}
                                    className="border-volt/30 text-volt text-xs h-8"
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Profil erstellen
                                  </Button>
                                </div>
                              )}
                            </div>
                            
                            {/* Add New Test Button */}
                            <Link
                              to={`/upload?athlete=${encodeURIComponent(athleteName)}`}
                              className="flex items-center gap-3 p-3 px-6 bg-volt/5 hover:bg-volt/10 transition-colors border-b border-white/10"
                            >
                              <Plus className="h-4 w-4 text-volt" />
                              <span className="text-sm text-volt font-medium">Neuen Test für {athleteName} hinzufügen</span>
                            </Link>

                            {/* Tests List */}
                            <div className="divide-y divide-white/5">
                              {athleteAnalyses.map((analysis, index) => (
                                <Link
                                  key={analysis.analysis_id}
                                  to={`/analysis/${analysis.analysis_id}`}
                                  className="flex items-center justify-between p-4 px-6 hover:bg-white/5 transition-colors"
                                >
                                  <div className="flex items-center gap-4">
                                    <div className="h-8 w-8 rounded-lg bg-zinc-800 flex items-center justify-center text-sm font-medium text-muted-foreground">
                                      {athleteAnalyses.length - index}
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium">
                                        {analysis.test_type === "20min" ? "20-Minuten Test" : "Stufentest"}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {/* Show test date if available, otherwise created_at */}
                                        {analysis.athlete_data?.test_date 
                                          ? formatDateLocal(analysis.athlete_data.test_date, {
                                              day: "2-digit",
                                              month: "long",
                                              year: "numeric"
                                            })
                                          : new Date(analysis.created_at).toLocaleDateString("de-DE", {
                                              day: "2-digit",
                                              month: "long",
                                              year: "numeric"
                                            })}
                                        {analysis.athlete_data?.test_time && (
                                          <span className="ml-1">• {analysis.athlete_data.test_time} Uhr</span>
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <div className="text-right">
                                      <p className="number-display text-lg text-volt">{Number(analysis.ftp).toFixed(2)}W</p>
                                      <p className="text-xs text-muted-foreground">{Number(analysis.ftp_per_kg).toFixed(2)} W/kg</p>
                                    </div>
                                    {index > 0 && (
                                      <div className="text-right w-16">
                                        {(() => {
                                          const prevFTP = athleteAnalyses[index - 1]?.ftp;
                                          const diff = analysis.ftp - prevFTP;
                                          return diff !== 0 ? (
                                            <span className={`text-xs ${diff > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                              {diff > 0 ? '+' : ''}{Number(diff).toFixed(2)}W
                                            </span>
                                          ) : null;
                                        })()}
                                      </div>
                                    )}
                                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            ) : (
              <div className="glass-card rounded-xl p-12 text-center">
                {hasActiveFilters ? (
                  <>
                    <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-display font-bold uppercase mb-2">Keine Ergebnisse</h3>
                    <p className="text-muted-foreground mb-4">
                      Keine Analysen gefunden. Versuche andere Suchbegriffe.
                    </p>
                    <Button onClick={clearFilters} variant="outline" className="border-white/20">
                      Filter zurücksetzen
                    </Button>
                  </>
                ) : (
                  <>
                    <TrendingUp className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-display font-bold uppercase mb-2">Keine Analysen</h3>
                    <p className="text-muted-foreground mb-4">
                      Starte jetzt mit deiner ersten FTP-Analyse.
                    </p>
                    <Link to="/upload" data-testid="empty-upload-link">
                      <Button className="skew-btn bg-volt text-black hover:bg-volt/90 font-bold uppercase tracking-wider">
                        <span>Erste Analyse starten</span>
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            )}
            </>
            )}
          </div>
        </div>
      </main>

      {/* Edit Athlete Profile Dialog */}
      <Dialog open={!!editingAthlete} onOpenChange={() => setEditingAthlete(null)}>
        <DialogContent className="sm:max-w-md bg-zinc-950 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">
              {athleteProfiles[editingAthlete] ? 'Athletenprofil bearbeiten' : 'Athletenprofil erstellen'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="text-sm text-muted-foreground mb-4">
              Profil für: <span className="text-white font-medium">{editingAthlete}</span>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="editBirthDate" className="text-xs text-muted-foreground mb-1 block">Geburtsdatum *</Label>
                <Input
                  id="editBirthDate"
                  type="date"
                  value={profileForm.birth_date}
                  onChange={(e) => setProfileForm({...profileForm, birth_date: e.target.value})}
                  className="bg-zinc-900 border-zinc-800 focus:border-volt"
                />
              </div>
              <div>
                <Label htmlFor="editGender" className="text-xs text-muted-foreground mb-1 block">Geschlecht</Label>
                <select
                  id="editGender"
                  value={profileForm.gender}
                  onChange={(e) => setProfileForm({...profileForm, gender: e.target.value})}
                  className="w-full h-10 px-3 bg-zinc-900 border border-zinc-800 rounded-md focus:border-volt focus:outline-none"
                >
                  <option value="male">Männlich</option>
                  <option value="female">Weiblich</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="editWeight" className="text-xs text-muted-foreground mb-1 block">Gewicht (kg) *</Label>
                <Input
                  id="editWeight"
                  type="number"
                  step="0.1"
                  value={profileForm.weight_kg}
                  onChange={(e) => setProfileForm({...profileForm, weight_kg: e.target.value})}
                  placeholder="75"
                  className="bg-zinc-900 border-zinc-800 focus:border-volt"
                />
              </div>
              <div>
                <Label htmlFor="editHeight" className="text-xs text-muted-foreground mb-1 block">Größe (cm) *</Label>
                <Input
                  id="editHeight"
                  type="number"
                  value={profileForm.height_cm}
                  onChange={(e) => setProfileForm({...profileForm, height_cm: e.target.value})}
                  placeholder="180"
                  className="bg-zinc-900 border-zinc-800 focus:border-volt"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingAthlete(null)}
              className="border-zinc-700"
            >
              Abbrechen
            </Button>
            <Button
              onClick={saveAthleteProfile}
              disabled={savingProfile}
              className="bg-volt text-black hover:bg-volt/90 font-bold"
            >
              {savingProfile ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
